(() => {
  'use strict';
  if (customElements.get('wnc-product-kit')) return;

  const normalize = value => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase().replace(/\s+/g, ' ');
  const containsColor = (text, color) => new RegExp(`(^|[^a-z0-9])${color.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^a-z0-9])`).test(text);
  const positiveInteger = value => Number.isInteger(Number(value)) && Number(value) > 0 ? Number(value) : 0;
  const sameId = (left, right) => String(left) === String(right);

  class WncProductKit extends HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.active = true;
      this.lifecycle = (this.lifecycle || 0) + 1;
      this.listeners = [];
      this.controllers = new Set();
      this.initialCartSeeded = false;
      try {
        this.config = JSON.parse(this.querySelector('[data-kit-config]').textContent);
      } catch (_) {
        this.active = false;
        return;
      }
      this.product = this.config.product || this.config;
      this.originalProductTitle = String(this.product.title || this.originalProductTitle || this.querySelector('[data-kit-title]')?.textContent || '');
      this.strings = this.config.strings || {};
      this.options = this.product.options || this.config.options || [];
      this.variants = this.product.variants || this.config.variants || [];
      this.tiers = (this.config.tiers || []).filter(tier => positiveInteger(tier.quantity));
      this.pieces = Array.from(this.querySelectorAll('[data-piece]'));
      this.tierIndex = Math.min(this.tierIndex || 0, Math.max(0, this.tiers.length - 1));
      if (!this.initialKitSelectionRead) {
        this.initialKitSelectionRead = true;
        try {
          const requestedKits = new URL(window.location.href).searchParams.getAll('kit');
          const requestedKit = requestedKits.length === 1 && /^[1-9]\d*$/.test(requestedKits[0]) ? Number(requestedKits[0]) : 0;
          if (Number.isSafeInteger(requestedKit) && requestedKit > 0 && requestedKit <= this.pieces.length) {
            const requestedTier = this.tiers.findIndex(tier => positiveInteger(tier.quantity) === requestedKit);
            if (requestedTier >= 0) this.tierIndex = requestedTier;
          }
        } catch (_) {}
      }
      this.sizePositions = this.options.map((option, index) => /(^|\W)(size|sizes|tamanho|tamanhos|talla|tallas|taille|taglia|tam|medida|medidas|numeracao)(\W|$)/.test(normalize(option.name)) ? index : -1).filter(index => index >= 0);
      this.colorPosition = this.options.findIndex(option => /(^|\W)(color|colour|colors|cor|cores|couleur|colore)(\W|$)/.test(normalize(option.name)));
      this.states = this.pieces.map((_, index) => this.states?.[index] || this.initialSelection());
      this.hasWizardMarkup = Boolean(this.querySelector('[data-kit-progress]'));
      this.currentStep = Number.isInteger(this.currentStep) ? this.currentStep : 0;
      this.form = this.querySelector('[data-kit-form]');
      this.cart = this.config.cart && Array.isArray(this.config.cart.items) ? this.config.cart : this.cart;
      this.media = Array.from(this.querySelectorAll('[data-media-id]'));
      this.mediaIndex = 0;
      this.zoomDialog = this.querySelector('[data-zoom-dialog]');
      this.sizeGuideObserver = new MutationObserver(() => this.placeSizeGuide());
      this.sizeGuideObserver.observe(this, { childList: true, subtree: true });
      this.bindGalleryGestures();
      this.listen(this, 'click', event => this.handleClick(event));
      if (this.form) this.listen(this.form, 'submit', event => { event.preventDefault(); this.addToCart(); });
      this.listen(document, 'wnc:cart-updated', event => {
        if (event.detail?.cart && Array.isArray(event.detail.cart.items)) {
          this.cart = event.detail.cart;
          this.render();
        } else this.refreshCart();
      });
      if (this.zoomDialog) {
        this.listen(this.zoomDialog, 'click', event => { if (event.target === this.zoomDialog) this.closeZoom(); });
        this.listen(this.zoomDialog, 'cancel', () => { this.zoomTrigger?.focus(); });
      }
      this.render();
      this.filterGallery();
      this.refreshCart();
    }

    disconnectedCallback() {
      this.active = false;
      this.lifecycle += 1;
      this.listeners?.forEach(([target, type, listener]) => target.removeEventListener(type, listener));
      this.controllers?.forEach(controller => controller.abort());
      this.sizeGuideObserver?.disconnect();
      this.restoreSizeGuide();
      this.listeners = [];
      this.closeZoom();
    }

    listen(target, type, listener) {
      target.addEventListener(type, listener);
      this.listeners.push([target, type, listener]);
    }

    text(key, values = {}) {
      return String(this.strings[key] || '').replace(/\{([a-z_]+)\}/g, (_, name) => String(values[name] ?? ''));
    }

    money(cents) {
      const amount = Math.round(Number(cents) || 0);
      try {
        return new Intl.NumberFormat(this.config.locale || document.documentElement.lang || 'pt-BR', {
          style: 'currency', currency: this.config.currency || 'BRL'
        }).format(amount / 100);
      } catch (_) {
        return (amount / 100).toFixed(2);
      }
    }

    get quantity() {
      return Math.min(positiveInteger(this.tiers[this.tierIndex]?.quantity) || 1, this.pieces.length);
    }

    get isWizard() {
      return this.hasWizardMarkup && this.quantity > 1;
    }

    get isReview() {
      return this.isWizard && this.currentStep === this.quantity;
    }

    validPiece(index) {
      return this.variantAvailable(this.resolveVariant(this.states[index]?.options), index);
    }

    firstInvalidPiece() {
      return this.states.slice(0, this.quantity).findIndex((_, index) => !this.validPiece(index));
    }

    initialSelection() {
      const selectedId = this.config.selectedVariantId || this.product.selectedVariantId;
      const initial = this.variants.find(variant => sameId(variant.id, selectedId) && variant.available) || this.variants.find(variant => variant.available) || this.variants[0];
      return { options: this.options.map((_, index) => this.sizePositions.includes(index) ? null : initial?.options?.[index] ?? null) };
    }

    resolveVariant(options) {
      if (!Array.isArray(options) || options.some(value => value === null || value === undefined)) return null;
      return this.variants.find(variant => variant.options?.length === options.length && variant.options.every((value, index) => String(value) === String(options[index]))) || null;
    }

    matchingVariants(options) {
      return this.variants.filter(variant => variant.options?.length === options.length && variant.options.every((value, index) => options[index] === null || options[index] === undefined || String(value) === String(options[index])));
    }

    optionAvailable(options, position, value, pieceIndex) {
      const candidate = options.slice();
      candidate[position] = value;
      if (!this.sizePositions.includes(position)) this.sizePositions.forEach(index => { candidate[index] = null; });
      return this.matchingVariants(candidate).some(variant => this.variantAvailable(variant, pieceIndex));
    }

    colorAvailable(value) {
      return this.variants.some(variant => variant.available && String(variant.options?.[this.colorPosition]) === String(value));
    }

    selectOption(pieceIndex, position, value) {
      const state = this.states[pieceIndex];
      if (!state || !this.options[position]) return;
      state.options[position] = value;
      if (!this.sizePositions.includes(position) && !this.variantAvailable(this.resolveVariant(state.options), pieceIndex)) {
        this.sizePositions.forEach(index => { state.options[index] = null; });
      }
    }

    inventoryLimit(variant) {
      if (!variant?.inventory_management || variant.inventory_policy === 'continue') return null;
      if (variant.inventory_quantity === null || variant.inventory_quantity === undefined || variant.inventory_quantity === '') return null;
      const quantity = Number(variant.inventory_quantity);
      return Number.isFinite(quantity) ? Math.max(0, quantity) : null;
    }

    cartQuantity(variantId) {
      return (this.cart?.items || []).reduce((sum, item) => sum + (sameId(item.variant_id ?? item.id, variantId) ? positiveInteger(item.quantity) : 0), 0);
    }

    variantAvailable(variant, pieceIndex = -1) {
      if (!variant?.available) return false;
      const limit = this.inventoryLimit(variant);
      const reserved = pieceIndex < 0 ? 0 : this.states.slice(0, this.quantity).reduce((sum, state, index) => sum + (index !== pieceIndex && sameId(this.resolveVariant(state.options)?.id, variant.id) ? 1 : 0), 0);
      return limit === null || limit > this.cartQuantity(variant.id) + reserved;
    }

    getSelection() {
      const variants = this.states.slice(0, this.quantity).map(state => this.resolveVariant(state.options));
      const groups = new Map();
      variants.forEach(variant => {
        if (!variant) return;
        const key = String(variant.id);
        if (groups.has(key)) groups.get(key).quantity += 1;
        else groups.set(key, { id: variant.id, sku: variant.sku || '', quantity: 1, weight: Number(variant.weight) || 0, price: Number(variant.price) || 0, variant });
      });
      let inventoryError = false;
      groups.forEach(item => {
        const limit = this.inventoryLimit(item.variant);
        if (!item.variant.available || (limit !== null && item.quantity + this.cartQuantity(item.id) > limit)) inventoryError = true;
      });
      const complete = variants.length > 0 && variants.every(Boolean);
      const prices = this.priceForTier(this.tierIndex);
      const items = Array.from(groups.values()).map(({ variant, ...item }) => item);
      return { variants, items, complete, inventoryError, ready: complete && !inventoryError, ...prices, quantity: this.quantity };
    }

    priceForTier(index) {
      const tier = this.tiers[index];
      const quantity = Math.min(positiveInteger(tier?.quantity) || 1, this.states.length);
      const variants = this.states.slice(0, quantity).map(state => this.resolveVariant(state.options) || this.matchingVariants(state.options).find(variant => variant.available) || this.matchingVariants(state.options)[0]);
      const baseTotal = variants.reduce((sum, variant) => sum + (Number(variant?.price) || 0), 0);
      const comparisonTotal = variants.reduce((sum, variant) => sum + Math.max(Number(variant?.price) || 0, Number(variant?.compare_at_price) || 0), 0);
      const configuredTotal = Number(tier?.totalCents ?? tier?.total);
      const baseUnitCents = positiveInteger(this.config.baseUnitCents) || 13900;
      const additions = variants.reduce((sum, variant) => sum + Math.max(0, (Number(variant?.price) || baseUnitCents) - baseUnitCents), 0);
      const totalCents = this.config.pricingEnabled && Number.isFinite(configuredTotal) && configuredTotal >= 0 ? Math.round(configuredTotal) + additions : baseTotal;
      return { baseTotal, comparisonTotal, totalCents, quantity };
    }

    getShippingItems() {
      return (this.shippingItems || []).map(item => ({ ...item }));
    }

    handleClick(event) {
      const sizeGuideLink = event.target.closest('[data-size-guide-open]');
      if (sizeGuideLink && this.contains(sizeGuideLink)) {
        const id = sizeGuideLink.getAttribute('href');
        const target = id?.startsWith('#') && document.getElementById(id.slice(1));
        if (target?.tagName === 'DETAILS') {
          event.preventDefault();
          target.open = true;
          this.scrollTarget(target);
          target.querySelector('summary')?.focus({ preventScroll: true });
          return;
        }
      }
      const button = event.target.closest('button');
      if (button?.matches('[data-zoom]') && Date.now() < (this.suppressZoomUntil || 0)) {
        event.preventDefault();
        return;
      }
      if (!button || !this.contains(button) || button.disabled) return;
      if (button.matches('[data-tier]')) {
        event.preventDefault();
        if (this.isSubmitting) return;
        const index = Number(button.dataset.tier);
        if (!this.tiers[index]) return;
        const wasReview = this.isReview;
        this.tierIndex = index;
        this.currentStep = this.isWizard ? Math.min(this.currentStep, this.quantity - 1) : 0;
        if (wasReview && this.isWizard) {
          const invalid = this.firstInvalidPiece();
          this.currentStep = invalid < 0 ? this.quantity : invalid;
        }
        this.setStatus('');
        this.render();
        this.filterGallery();
      } else if (button.matches('[data-piece-step]')) {
        event.preventDefault();
        if (this.isSubmitting) return;
        const index = Number(button.dataset.pieceStep);
        if (!Number.isInteger(index) || index < 0 || index >= this.quantity) return;
        this.currentStep = index;
        this.setStatus('');
        this.render();
        this.filterGallery();
        this.focusCurrentStep();
      } else if (button.matches('[data-step-previous]')) {
        event.preventDefault();
        if (this.isSubmitting || !this.isWizard) return;
        this.currentStep = Math.max(0, this.currentStep - 1);
        this.setStatus('');
        this.render();
        this.filterGallery();
        this.focusCurrentStep();
      } else if (button.matches('[data-step-next]')) {
        event.preventDefault();
        if (!this.isSubmitting) this.nextStep();
      } else if (button.matches('[data-option-value]')) {
        event.preventDefault();
        if (this.isSubmitting) return;
        const piece = button.closest('[data-piece]');
        const index = this.pieces.indexOf(piece);
        const position = Number(button.dataset.optionPosition);
        if (index < 0 || index >= this.quantity || !this.options[position]) return;
        this.selectOption(index, position, button.dataset.optionValue);
        this.setStatus('');
        this.render();
        if (index === 0 || this.isWizard && index === this.currentStep) this.filterGallery();
      } else if (button.matches('[data-sticky-add]')) {
        event.preventDefault();
        if (this.form?.requestSubmit) this.form.requestSubmit();
        else this.addToCart();
      } else if (button.matches('[data-media-target]')) {
        event.preventDefault();
        const index = this.visibleMedia?.findIndex(media => sameId(media.dataset.mediaId, button.dataset.mediaTarget));
        if (index >= 0) this.showMedia(index);
      } else if (button.matches('[data-gallery-prev], [data-gallery-next]')) {
        event.preventDefault();
        this.showMedia(this.mediaIndex + (button.matches('[data-gallery-prev]') ? -1 : 1));
      } else if (button.matches('[data-zoom]')) {
        event.preventDefault();
        this.openZoom(button);
      } else if (button.matches('[data-zoom-close]')) {
        event.preventDefault();
        this.closeZoom();
      }
    }

    render() {
      const kitTitleTemplate = String(this.config.kitTitleTemplate || '');
      const title = this.quantity > 1 && kitTitleTemplate.trim() ? kitTitleTemplate.replace(/\{quantity\}/g, String(this.quantity)) : this.originalProductTitle;
      this.querySelectorAll('[data-kit-title]').forEach(target => { if (target.textContent !== title) target.textContent = title; });
      const selection = this.getSelection();
      this.selection = selection;
      this.shippingItems = selection.items;
      this.currentStep = this.isWizard ? Math.min(Math.max(0, this.currentStep || 0), this.quantity) : 0;
      this.querySelectorAll('[data-tier]').forEach(button => {
        const active = Number(button.dataset.tier) === this.tierIndex;
        button.setAttribute('aria-pressed', String(active));
        button.classList.toggle('is-selected', active);
        button.disabled = Boolean(this.isSubmitting);
        const prices = this.priceForTier(Number(button.dataset.tier));
        button.querySelectorAll('[data-tier-unit]').forEach(target => { target.textContent = this.money(prices.totalCents / prices.quantity); });
        button.querySelectorAll('[data-tier-total]').forEach(target => { target.textContent = this.money(prices.totalCents); });
        button.querySelectorAll('[data-tier-saving]').forEach(target => {
          const saving = Math.max(0, prices.baseTotal - prices.totalCents);
          target.hidden = saving <= 0;
          target.textContent = this.text('save', { amount: this.money(saving) });
        });
      });
      this.pieces.forEach((piece, index) => {
        piece.hidden = index >= this.quantity || this.isWizard && index !== this.currentStep;
        if (piece.hidden) return;
        const state = this.states[index];
        const variant = selection.variants[index];
        piece.querySelectorAll('[data-option-value]').forEach(button => {
          const position = Number(button.dataset.optionPosition);
          button.hidden = position === this.colorPosition && !this.colorAvailable(button.dataset.optionValue);
          const selected = state.options[position] === button.dataset.optionValue;
          const available = this.optionAvailable(state.options, position, button.dataset.optionValue, index);
          button.setAttribute('aria-pressed', String(selected));
          button.classList.toggle('is-selected', selected);
          button.classList.toggle('is-unavailable', !available);
          button.disabled = Boolean(this.isSubmitting) || !available;
          button.setAttribute('aria-disabled', String(button.disabled));
        });
        piece.querySelectorAll('[data-option-current]').forEach(current => {
          const position = Number(current.dataset.optionCurrent);
          current.textContent = state.options[position] ?? this.text(this.sizePositions.includes(position) ? 'choose_size' : 'choose_options');
        });
        const summary = piece.querySelector('[data-piece-summary]');
        if (summary) summary.textContent = variant ? this.text('piece_selected', { options: variant.options.join(' / ') }) : this.text('piece_missing', { number: index + 1 });
        const image = piece.querySelector('[data-piece-image]');
        const displayVariant = variant || this.matchingVariants(state.options).find(item => item.available) || this.matchingVariants(state.options)[0];
        this.setSelectionImage(image, displayVariant, state.options);
        piece.classList.toggle('is-complete', Boolean(variant?.available));
      });
      this.querySelectorAll('[data-total]:not([data-tier])').forEach(target => { target.textContent = this.money(selection.totalCents); });
      this.querySelectorAll('[data-unit-price]').forEach(target => { target.textContent = this.money(selection.totalCents / this.quantity); });
      const saving = Math.max(0, selection.baseTotal - selection.totalCents);
      this.querySelectorAll('[data-compare-price]').forEach(target => { target.hidden = selection.comparisonTotal <= selection.totalCents; target.textContent = this.money(selection.comparisonTotal); });
      this.querySelectorAll('[data-saving]').forEach(target => { target.hidden = saving <= 0; target.textContent = this.text('save', { amount: this.money(saving) }); });
      const installmentCount = positiveInteger(this.config.installmentsCount) || 6;
      this.querySelectorAll('[data-installments]').forEach(target => { target.textContent = this.text('installments', { count: installmentCount, amount: this.money(selection.totalCents / installmentCount) }); });
      this.querySelectorAll('[data-add-to-cart], [data-sticky-add]').forEach(button => {
        button.disabled = !this.variants.some(variant => variant.available) || Boolean(this.isSubmitting);
        button.setAttribute('aria-disabled', String(button.disabled));
        button.setAttribute('aria-busy', String(Boolean(this.isSubmitting)));
      });
      const labelKey = this.isSubmitting ? 'adding' : !this.variants.some(variant => variant.available) ? 'unavailable' : this.isWizard && !this.isReview ? this.currentStep === this.quantity - 1 ? 'review_kit' : 'next_piece' : 'add_to_cart';
      this.querySelectorAll('[data-add-label]').forEach(target => { target.textContent = this.text(labelKey); });
      this.querySelectorAll('[data-sticky-label]').forEach(target => { target.textContent = this.text(labelKey); });
      this.renderSteps(selection);
      this.placeSizeGuide();
      const firstVariant = selection.variants[0];
      this.querySelectorAll('input[data-variant-id]').forEach(input => { input.value = firstVariant?.id || ''; input.disabled = !selection.ready || Boolean(this.isSubmitting); });
      this.querySelectorAll('input[data-quantity]').forEach(input => { input.value = this.quantity; input.disabled = !selection.ready || Boolean(this.isSubmitting); });
      if (!this.isSubmitting && selection.inventoryError) this.setStatus(this.text('inventory_error'), true);
      else if (!this.isSubmitting && this.querySelector('[data-kit-status]')?.textContent === this.text('inventory_error')) this.setStatus('');
      this.updateUrl(firstVariant?.id);
      const detail = { sectionId: String(this.config.sectionId || this.dataset.sectionId || ''), items: this.getShippingItems(), totalCents: selection.totalCents, quantity: this.quantity, ready: selection.ready };
      const signature = JSON.stringify(detail);
      if (signature !== this.lastSelectionSignature) {
        this.lastSelectionSignature = signature;
        this.dispatchEvent(new CustomEvent('wnc:kit-change', { bubbles: true, detail }));
      }
    }

    renderSteps(selection) {
      this.classList.toggle('is-kit-wizard', Boolean(this.isWizard));
      this.classList.toggle('is-kit-review', Boolean(this.isReview));
      this.querySelectorAll('[data-piece-stepper], [data-step-navigation], [data-kit-step-navigation]').forEach(target => { target.hidden = !this.isWizard; });
      this.querySelectorAll('[data-kit-review]').forEach(target => { target.hidden = !this.isReview; });
      this.querySelectorAll('[data-step-current]').forEach(target => { target.textContent = Math.min(this.currentStep + 1, this.quantity); });
      this.querySelectorAll('[data-step-total]').forEach(target => { target.textContent = this.quantity; });
      this.querySelectorAll('[data-step-previous]').forEach(button => {
        button.hidden = !this.isWizard || this.currentStep === 0;
        button.disabled = Boolean(this.isSubmitting);
      });
      this.querySelectorAll('[data-step-next]').forEach(button => {
        button.hidden = !this.isWizard || this.isReview;
        button.disabled = Boolean(this.isSubmitting) || !this.variants.some(variant => variant.available);
      });
      this.querySelectorAll('[data-step-next-label]').forEach(target => { target.textContent = this.text(this.currentStep === this.quantity - 1 ? 'review_kit' : 'next_piece'); });
      this.querySelectorAll('[data-piece-step]').forEach(button => {
        const index = Number(button.dataset.pieceStep);
        const active = this.isWizard && !this.isReview && index === this.currentStep;
        button.hidden = index >= this.quantity;
        button.disabled = Boolean(this.isSubmitting);
        button.setAttribute('aria-current', active ? 'step' : 'false');
        button.setAttribute('aria-pressed', String(active));
        button.classList.toggle('is-current', active);
        button.classList.toggle('is-complete', index < this.quantity && this.validPiece(index));
        const variant = selection.variants[index];
        button.querySelectorAll('[data-step-summary]').forEach(target => { target.textContent = variant ? this.text('piece_selected', { options: variant.options.join(' / ') }) : this.text('piece_missing', { number: index + 1 }); });
      });
      this.querySelectorAll('[data-review-piece]').forEach(row => {
        const index = Number(row.dataset.reviewPiece);
        row.hidden = index >= this.quantity;
        if (row.hidden) return;
        const variant = selection.variants[index];
        row.classList.toggle('is-complete', this.validPiece(index));
        row.querySelectorAll('[data-review-summary]').forEach(target => { target.textContent = variant ? this.text('piece_selected', { options: variant.options.join(' / ') }) : this.text('piece_missing', { number: index + 1 }); });
        const image = row.querySelector('[data-summary-image]');
        this.setSelectionImage(image, variant, this.states[index]?.options);
      });
    }

    nextStep() {
      if (!this.isWizard || this.isReview || this.isSubmitting) return false;
      if (!this.validPiece(this.currentStep)) {
        const variant = this.resolveVariant(this.states[this.currentStep]?.options);
        this.setStatus(this.text(variant ? 'inventory_error' : 'selection_error'), true);
        this.focusCurrentStep(true);
        return false;
      }
      if (this.currentStep === this.quantity - 1 && !this.getSelection().ready) {
        this.setStatus(this.text('selection_error'), true);
        this.focusMissingSelection();
        return false;
      }
      this.currentStep += 1;
      this.setStatus('');
      this.render();
      this.filterGallery();
      this.focusCurrentStep();
      return true;
    }

    focusCurrentStep(highlightMissing = false) {
      if (this.isReview) {
        const heading = this.querySelector('[data-review-heading]') || this.querySelector('[data-kit-review]');
        this.scrollTarget(heading);
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
        return;
      }
      this.focusPieceSelection(this.currentStep || 0, highlightMissing);
    }

    focusPieceSelection(index, highlightMissing = false) {
      const piece = this.pieces[index];
      if (!piece) return;
      const options = this.states[index]?.options || [];
      const missingSizePosition = this.sizePositions.find(position => options[position] === null || options[position] === undefined);
      const position = missingSizePosition ?? options.findIndex(value => value === null || value === undefined);
      const selector = position >= 0 ? `[data-option-position="${position}"]:not(:disabled)` : '[data-option-value]:not(:disabled)';
      const target = piece.querySelector(selector);
      if (highlightMissing && missingSizePosition !== undefined) {
        const group = (target || piece.querySelector(`[data-option-position="${position}"]`))?.closest('fieldset');
        if (group && typeof window.wncHighlightSizeGroup === 'function') { window.wncHighlightSizeGroup(group); return; }
      }
      this.scrollTarget(piece);
      target?.focus({ preventScroll: true });
    }

    scrollTarget(target) {
      target?.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'instant' : 'smooth', block: 'center' });
    }

    updateUrl(variantId) {
      if (window.Shopify?.designMode) return;
      try {
        const url = new URL(window.location.href);
        if (variantId) url.searchParams.set('variant', variantId);
        if (this.quantity > 1 || url.searchParams.has('kit')) url.searchParams.set('kit', this.quantity);
        const nextUrl = url.toString();
        if (nextUrl !== window.location.href) window.history.replaceState(window.history.state, '', nextUrl);
      } catch (_) {}
    }

    setStatus(message, error = false) {
      const status = this.querySelector('[data-kit-status]');
      if (!status) return;
      status.textContent = message;
      status.hidden = !message;
      status.classList.toggle('is-error', Boolean(message && error));
    }

    route(name, suffix) {
      const configured = this.config.routes?.[name] || this.config.routes?.[`${name}Url`];
      if (configured) return configured;
      const root = window.Shopify?.routes?.root || '/';
      return `${root.replace(/\/$/, '')}/${suffix}`;
    }

    async refreshCart() {
      if (!this.active) return null;
      const lifecycle = this.lifecycle;
      const controller = new AbortController();
      this.controllers.add(controller);
      const timer = setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(this.route('cartJson', 'cart.js'), { headers: { Accept: 'application/json' }, credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) return null;
        const cart = await response.json();
        if (!Array.isArray(cart.items) || lifecycle !== this.lifecycle || !this.active) return null;
        this.cart = cart;
        if (!this.initialCartSeeded) {
          this.initialCartSeeded = true;
          const seed = () => {
            if (!this.active || lifecycle !== this.lifecycle) return;
            this.lastSelectionSignature = null;
            this.render();
          };
          if (document.readyState === 'loading') this.listen(document, 'DOMContentLoaded', seed);
          else seed();
        } else this.render();
        return cart;
      } catch (_) {
        return null;
      } finally {
        clearTimeout(timer);
        this.controllers.delete(controller);
      }
    }

    async addToCart() {
      if (this.isSubmitting || !this.active) return;
      if (this.isWizard && !this.isReview) {
        this.nextStep();
        return;
      }
      let selection = this.getSelection();
      if (!selection.ready) {
        this.setStatus(this.text(selection.inventoryError ? 'inventory_error' : 'selection_error'), true);
        this.focusMissingSelection();
        return;
      }
      this.isSubmitting = true;
      this.setStatus('');
      this.render();
      await this.refreshCart();
      if (!this.active) { this.isSubmitting = false; return; }
      selection = this.getSelection();
      if (!selection.ready) {
        this.isSubmitting = false;
        this.render();
        this.setStatus(this.text('inventory_error'), true);
        this.focusMissingSelection();
        return;
      }
      const discountCode = this.cartDiscountCode(selection);
      const destination = this.cartDestination(selection);
      const payload = { items: selection.items.map(item => ({ id: item.id, quantity: item.quantity })) };
      let committed = false;
      try {
        const response = await fetch(this.route('cartAdd', 'cart/add.js'), {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(payload)
        });
        let data = null;
        try { data = await response.json(); } catch (_) {}
        if (!response.ok || data?.status >= 400 || data?.errors) {
          const message = typeof data?.description === 'string' ? data.description : typeof data?.message === 'string' ? data.message : this.text('cart_error');
          this.setStatus(message, true);
          this.refreshCart();
          return;
        }
        committed = true;
        const cart = discountCode ? await this.applyCartDiscount(discountCode) : null;
        if (!this.active) return;
        this.setStatus(this.text('added'));
        document.dispatchEvent(new CustomEvent('wnc:cart-updated', { detail: { source: 'wnc-product-kit', items: selection.items, ...(cart ? { cart } : {}) } }));
        window.location.assign(cart ? this.route('cart', 'cart') : destination);
      } catch (_) {
        if (!this.active) return;
        this.setStatus(this.text(committed ? 'added' : 'network_error'), !committed);
        if (committed) window.location.href = destination;
      } finally {
        if (!committed) {
          this.isSubmitting = false;
          if (this.active) this.render();
        }
      }
    }

    cartDiscountCode(selection) {
      if (!this.config.pricingEnabled) return null;
      const variantIds = new Set(this.variants.map(variant => String(variant.id)));
      const cartQuantity = (this.cart?.items || []).reduce((sum, item) => sum + (variantIds.has(String(item.variant_id ?? item.id)) ? positiveInteger(item.quantity) : 0), 0);
      const finalQuantity = cartQuantity + positiveInteger(selection?.quantity ?? this.quantity);
      const tier = this.tiers.filter(item => item.discountCode && positiveInteger(item.quantity) <= finalQuantity).reduce((best, item) => !best || positiveInteger(item.quantity) > positiveInteger(best.quantity) ? item : best, null);
      return tier?.discountCode || null;
    }

    async applyCartDiscount(code) {
      const knownCodes = new Set(this.tiers.map(tier => String(tier.discountCode || '').toLowerCase()).filter(Boolean));
      const codes = (this.cart?.discount_codes || []).map(item => typeof item === 'string' ? item : item?.code).filter(item => typeof item === 'string' && item.trim() && !knownCodes.has(item.toLowerCase()));
      codes.push(code);
      const controller = new AbortController();
      this.controllers.add(controller);
      const timer = setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(this.route('cartUpdate', 'cart/update.js'), {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ discount: [...new Set(codes)].join(',') }), signal: controller.signal
        });
        const cart = await response.json();
        if (!response.ok || cart?.status >= 400 || cart?.errors || !Array.isArray(cart?.items) || !Number.isFinite(cart.total_price)) throw new Error('discount_update_failed');
        this.cart = cart;
        return cart;
      } finally {
        clearTimeout(timer);
        this.controllers.delete(controller);
      }
    }

    cartDestination(selection) {
      const cart = this.route('cart', 'cart');
      const code = this.cartDiscountCode(selection);
      if (!code) return cart;
      const root = this.config.routes?.root || window.Shopify?.routes?.root || '/';
      return `${root.replace(/\/$/, '')}/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(cart)}`;
    }

    focusMissingSelection() {
      for (let index = 0; index < this.quantity; index += 1) {
        const position = this.states[index].options.findIndex(value => value === null || value === undefined);
        const variant = this.resolveVariant(this.states[index].options);
        if (position >= 0 || !this.variantAvailable(variant, index)) {
          if (this.isWizard) {
            this.currentStep = index;
            this.render();
            this.filterGallery();
          }
          this.focusPieceSelection(index, true);
          return;
        }
      }
    }

    galleryColors() {
      return this.colorPosition >= 0 ? (this.options[this.colorPosition].values || []).map(value => normalize(typeof value === 'object' ? value.name : value)) : [];
    }

    colorMediaMap() {
      return new Map(Object.entries(this.config.colorMediaMap || {}).map(([color, ids]) => [normalize(color), Array.isArray(ids) ? ids.map(String) : []]));
    }

    mediaForOptions(options) {
      const colors = this.galleryColors();
      const color = this.colorPosition >= 0 ? normalize(options?.[this.colorPosition]) : '';
      const map = this.colorMediaMap();
      if (map.has(color)) {
        const ids = new Set([...map.get(color), ...(map.get('*') || [])]);
        return this.media.filter(media => ids.has(String(media.dataset.mediaId)));
      }
      if (colors.length < 2) return this.media;
      const assignments = this.galleryAssignments(colors);
      return this.media.filter((media, index) => assignments[index].includes(color) || assignments[index].includes('*'));
    }

    filterGallery() {
      if (!this.media?.length) return;
      const state = this.states[this.isWizard && !this.isReview ? this.currentStep : 0];
      const variant = this.resolveVariant(state.options) || this.matchingVariants(state.options).find(item => item.available);
      this.closeZoom();
      this.visibleMedia = this.mediaForOptions(state.options);
      this.querySelectorAll('[data-media-target]').forEach(thumb => { thumb.hidden = !this.visibleMedia.some(media => sameId(media.dataset.mediaId, thumb.dataset.mediaTarget)); });
      this.querySelectorAll('[data-gallery-empty]').forEach(target => { target.hidden = this.visibleMedia.length > 0; });
      this.querySelectorAll('.wnc-kit__gallery-controls').forEach(target => { target.hidden = this.visibleMedia.length === 0; });
      this.querySelectorAll('.wnc-kit__thumbnails').forEach(target => { target.hidden = this.visibleMedia.length < 2; });
      if (!this.visibleMedia.length) {
        this.mediaIndex = 0;
        this.media.forEach(media => {
          media.hidden = true;
          media.setAttribute('aria-hidden', 'true');
          media.querySelectorAll('video').forEach(video => video.pause());
        });
        this.querySelectorAll('[data-gallery-count]').forEach(target => { target.textContent = this.text('gallery_count', { current: 0, total: 0 }); });
        this.querySelectorAll('[data-gallery-prev], [data-gallery-next]').forEach(button => { button.disabled = true; });
        return;
      }
      const featuredId = variant?.featured_media?.id;
      const featuredIndex = this.visibleMedia.findIndex(media => sameId(media.dataset.mediaId, featuredId));
      this.showMedia(featuredIndex >= 0 ? featuredIndex : 0);
    }

    colorsInText(value, colors, compact = false) {
      const source = normalize(value).replace(/[-_]+/g, ' ');
      const compactSource = source.replace(/[^a-z0-9]/g, '');
      const matches = colors.filter(color => {
        if (!color) return false;
        const aliases = [color, color.replace(/\b(preto|branco|vermelho|amarelo|roxo|cinzento|estonado)\b/g, word => `${word.slice(0, -1)}a`)];
        return aliases.some(alias => containsColor(source, alias) || compact && compactSource.includes(alias.replace(/[^a-z0-9]/g, '')));
      });
      return matches.filter(color => !matches.some(longer => longer.length > color.length && containsColor(longer, color)));
    }

    galleryAssignments(colors) {
      const map = this.colorMediaMap();
      const variantColors = new Map(this.variants.map(variant => [String(variant.id), normalize(variant.options?.[this.colorPosition])]));
      return this.media.map(media => {
        const id = String(media.dataset.mediaId);
        const mappedColors = Array.from(map).filter(([color, ids]) => (colors.includes(color) || color === '*') && ids.includes(id)).map(([color]) => color);
        if (mappedColors.length) return mappedColors;
        const attached = Array.from(new Set((media.dataset.mediaVariantIds || '').split(',').map(value => variantColors.get(value.trim())).filter(color => colors.includes(color) && !map.has(color))));
        if (attached.length) return attached;
        const uncoveredColors = colors.filter(color => !map.has(color));
        const altColors = this.colorsInText(media.dataset.mediaAlt || '', uncoveredColors);
        if (altColors.length) return altColors;
        const image = media.querySelector('img');
        const source = image?.currentSrc || image?.src || '';
        let filename = source.split(/[?#]/)[0].split('/').pop() || '';
        try { filename = decodeURIComponent(filename); } catch (_) {}
        return this.colorsInText(filename, uncoveredColors, true);
      });
    }

    setSelectionImage(image, variant, options) {
      if (!image) return;
      const media = this.mediaForOptions(options);
      const featured = media.find(item => sameId(item.dataset.mediaId, variant?.featured_media?.id));
      const selected = featured?.querySelector('img') || media.map(item => item.querySelector('img')).find(Boolean);
      const source = selected?.currentSrc || selected?.src;
      image.hidden = !source;
      if (source) {
        image.src = source;
        image.alt = selected.alt || '';
        image.removeAttribute('srcset');
      }
    }

    bindGalleryGestures() {
      const stage = this.querySelector('[data-gallery-stage]');
      if (!stage) return;
      this.listen(stage, 'pointerdown', event => {
        this.galleryPointer = event.pointerType !== 'mouse' && event.target.closest('[data-zoom]') ? { id: event.pointerId, x: event.clientX, y: event.clientY } : null;
      });
      this.listen(stage, 'pointercancel', () => { this.galleryPointer = null; });
      this.listen(stage, 'pointerup', event => {
        const start = this.galleryPointer;
        this.galleryPointer = null;
        if (!start || start.id !== event.pointerId || this.visibleMedia?.length < 2) return;
        const horizontal = event.clientX - start.x;
        const vertical = event.clientY - start.y;
        if (Math.abs(horizontal) < 50 || Math.abs(horizontal) < Math.abs(vertical) * 1.4) return;
        this.suppressZoomUntil = Date.now() + 400;
        this.showMedia(this.mediaIndex + (horizontal > 0 ? -1 : 1));
      });
    }

    restoreSizeGuide() {
      const home = this.sizeGuideHome;
      const node = this.sizeGuideNode;
      if (!home || !node || node.parentNode === home.parent) return;
      home.parent.insertBefore(node, home.next?.parentNode === home.parent ? home.next : null);
    }

    placeSizeGuide() {
      const node = this.querySelector('#ccpops-trigger-container');
      if (!node) return;
      if (node !== this.sizeGuideNode) {
        this.sizeGuideNode = node;
        this.sizeGuideHome = { parent: node.parentNode, next: node.nextSibling };
      }
      const slot = this.pieces.find(piece => !piece.hidden)?.querySelector('[data-size-guide-slot]');
      if (slot && node.parentNode !== slot) slot.appendChild(node);
      else if (!slot) this.restoreSizeGuide();
    }

    showMedia(index) {
      if (!this.visibleMedia?.length) return;
      this.mediaIndex = (index + this.visibleMedia.length) % this.visibleMedia.length;
      const current = this.visibleMedia[this.mediaIndex];
      this.media.forEach(media => {
        const active = media === current;
        media.hidden = !active;
        media.setAttribute('aria-hidden', String(!active));
        if (!active) media.querySelectorAll('video').forEach(video => video.pause());
      });
      this.querySelectorAll('[data-media-target]').forEach(thumb => {
        const selected = sameId(thumb.dataset.mediaTarget, current.dataset.mediaId);
        thumb.setAttribute('aria-current', String(selected));
        thumb.setAttribute('aria-pressed', String(selected));
        thumb.classList.toggle('is-selected', selected);
      });
      this.querySelectorAll('[data-gallery-count]').forEach(target => { target.textContent = this.text('gallery_count', { current: this.mediaIndex + 1, total: this.visibleMedia.length }); });
      this.querySelectorAll('[data-gallery-prev], [data-gallery-next]').forEach(button => { button.disabled = this.visibleMedia.length < 2; });
    }

    openZoom(trigger) {
      const image = this.querySelector('[data-zoom-image]');
      const source = trigger.dataset.src || trigger.dataset.full || trigger.querySelector('img')?.currentSrc || trigger.querySelector('img')?.src;
      if (!this.zoomDialog || !image || !source) return;
      this.zoomTrigger = trigger;
      image.src = source;
      image.alt = trigger.dataset.alt || trigger.querySelector('img')?.alt || '';
      if (typeof this.zoomDialog.showModal === 'function') this.zoomDialog.showModal();
      else this.zoomDialog.setAttribute('open', '');
      this.zoomDialog.querySelector('[data-zoom-close]')?.focus();
    }

    closeZoom() {
      if (!this.zoomDialog?.open) return;
      if (typeof this.zoomDialog.close === 'function') this.zoomDialog.close();
      else this.zoomDialog.removeAttribute('open');
      if (this.zoomTrigger?.isConnected) this.zoomTrigger.focus();
    }
  }

  customElements.define('wnc-product-kit', WncProductKit);
})();




